import JsSIP from 'jssip';
import extensionService from './extensionService';

class WebRTCService {
  constructor() {
    this.ua = null;
    this.currentSession = null;
    this.incomingSession = null;
    this.localAudio = null;
    this.remoteAudio = null;
    this.isConnected = false;
    this.isRegistered = false;
    this.dtmfSender = null;
    this.callStartTime = null;
    this.userExten = null;
    this.sipUser = null;
    this.sipDomain = null;
    this.userOutboundCid = null;
    this.ringbackAudio = null; // Audio element for ringback tone
    this.ringtoneAudio = null;
    this._debugSeq = 0;
    this._lastOutboundCallDebugId = null;
    this._initGeneration = 0;
    this._outboundStream = null;
    this._playbackStream = null;
    this._dialing = false;
    this._campaignActive = false;
    
    // Callbacks
    this.onCallStarted = null;
    this.onCallConnected = null;
    this.onCallEnded = null;
    this.onCallFailed = null;
    this.onRegistered = null;
    this.onUnregistered = null;
    this.onRegistrationFailed = null;
    this.onDTMFSent = null;
    this.onDTMFReceived = null; // Callback for received DTMF from remote caller
    this.onIncomingCall = null;
    this.onIncomingCallEnded = null;
    this.onOccupancyChange = null;
    this.onStopCampaign = null;
  }

  async init(config) {
    const initId = ++this._initGeneration;

    if (this.ua && config.exten && this.userExten === config.exten) {
      console.log('ℹ️ WebRTC já inicializado para', config.exten);
      if (this.isRegistered && this.onRegistered) {
        this.onRegistered();
      }
      return { success: true };
    }

    if (this.ua) {
      this.disconnect();
      this._initGeneration = initId;
    }

    console.log('🚀 Inicializando WebRTC Service...');
    
    try {
      // If exten is provided, fetch SIP credentials from pjsip_magnus_user.conf via backend
      let user = config.user;
      let password = config.password;
      let displayName = config.displayName || config.user;
      
      if (config.exten) {
        console.log('📞 Fetching extension credentials for exten:', config.exten);
        try {
          const extensionData = await extensionService.fetchExtension(config.exten);
          if (initId !== this._initGeneration) {
            console.log('ℹ️ Init WebRTC ignorado (nova inicialização em andamento)');
            return { success: false, error: 'superseded' };
          }
          user = extensionData.extensionId;
          password = extensionData.user.extPassword;
          displayName = extensionData.user.name || extensionData.extensionId;
          this.userExten = extensionData.extensionId;
          this.userOutboundCid = extensionData.user.outboundCid;
          console.log('✅ Extension credentials fetched successfully');
        } catch (error) {
          console.error('❌ Error fetching extension credentials:', error);
          return { success: false, error: `Failed to fetch extension credentials: ${error.message}` };
        }
      } else {
        this.userExten = config.user;
      }

    // SIP signaling identity for Asterisk WSS endpoints (pjsip_magnus_user_wss.conf).
    // Login/UI remain the Magnus extension (1234); register + INVITE use 1234-wss.
    const sipUser = `${user}-wss`;
    this.sipUser = sipUser;
    this.sipDomain = config.server;

    // Configuração para servidor SIP via proxy FastAPI
    // Usa WebSocket através do backend que faz proxy para o servidor SIP
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    // const wsUrl = `${wsProtocol}://${wsHost}/ws/sip`;
     const wsUrl = `wss://diskbot.com.br:8099/ws`;
    
    console.log('🔗 Conectando em:', wsUrl);
    console.log('📞 SIP WSS identity:', sipUser, 'display/login exten:', this.userExten);
    const socket = new JsSIP.WebSocketInterface(wsUrl);
    socket.via_transport = "WSS";
    const configuration = {
      sockets: [socket],
      uri: `sip:${sipUser}@${config.server}`,
      authorization_user: sipUser,
      password: password,
      register: true,
      session_timers: false,
      no_answer_timeout: 60,
      display_name: displayName,
      // Enable DTMF handling - try to receive via both SIP INFO and RFC2833
      // Note: JsSIP handles SIP INFO DTMF automatically, but RFC2833 requires manual parsing
    };

    try {
      this.ua = new JsSIP.UA(configuration);
      
      // Event handlers
      this.ua.on('connected', (e) => {
        console.log('✅ WebSocket conectado');
        this.isConnected = true;
      });

      this.ua.on('disconnected', (e) => {
        console.log('❌ WebSocket desconectado');
        this.isConnected = false;
      });

      this.ua.on('registered', (e) => {
        console.log('✅ SIP Registrado com sucesso');
        this.isRegistered = true;
        if (this.onRegistered) this.onRegistered();
      });

      this.ua.on('unregistered', (e) => {
        console.log('❌ SIP Não registrado');
        this.isRegistered = false;
        if (this.onUnregistered) this.onUnregistered();
      });

      this.ua.on('registrationFailed', (e) => {
        console.error('❌ Falha no registro SIP:', e.cause);
        this.isRegistered = false;
        if (this.onRegistrationFailed) this.onRegistrationFailed(e.cause);
      });

      // Listen for all UA-level events that might contain DTMF
      this.ua.on('newRTCSession', (data) => {
        const debugId = this._nextDebugId('rtc');
        data.session.__debugId = debugId;
        if (this._lastOutboundCallDebugId) {
          data.session.__outboundDebugId = this._lastOutboundCallDebugId;
        }
        this._dbg(data.session, '📞 Nova sessão RTC', {
          originator: data.originator,
          direction: data.session?.direction,
          hasRequest: !!data.request,
          outboundDebugId: data.session.__outboundDebugId || null
        });

        if (data.originator === 'remote') {
          this.handleIncomingSession(data.session);
          return;
        }

        this.handleSession(data.session);
      });

      // Also listen for SIP messages at UA level to catch DTMF INFO messages
      this.ua.on('newMessage', (e) => {
        console.log('📨 UA newMessage event:', e);
        if (e.message && e.message.request && e.message.request.method === 'INFO') {
          console.log('📨 SIP INFO message received at UA level:', e.message);
          const body = e.message.request.body || '';
          const contentType = e.message.request.getHeader('Content-Type') || '';
          console.log('📨 Content-Type:', contentType);
          console.log('📨 Body:', body);
          
          if (contentType.toLowerCase().includes('dtmf')) {
            console.log('✅ DTMF detected in UA-level INFO message!');
            // Try to extract digit
            const patterns = [
              /Signal[=\s:]*([0-9*#A-D])/i,
              /Key[=\s:]*([0-9*#A-D])/i,
              /Digit[=\s:]*([0-9*#A-D])/i,
              /Code[=\s:]*([0-9*#A-D])/i,
              /([0-9*#A-D])/
            ];
            
            for (const pattern of patterns) {
              const match = body.match(pattern);
              if (match && match[1]) {
                const dtmfDigit = match[1];
                console.log('✅ DTMF digit extraído do UA-level INFO:', dtmfDigit);
                console.log('✅ onDTMFReceived callback exists?', !!this.onDTMFReceived);
                if (this.onDTMFReceived) {
                  try {
                    this.onDTMFReceived(dtmfDigit);
                    console.log('✅ onDTMFReceived callback chamado com sucesso (UA-level)');
                  } catch (error) {
                    console.error('❌ Erro ao chamar onDTMFReceived:', error);
                  }
                } else {
                  console.error('❌ onDTMFReceived callback não está definido!');
                }
                return;
              }
            }
          }
        }
      });

      this.ua.start();
      console.log('✅ JsSIP UA iniciado');
      
      return { success: true };
    } catch (error) {
      console.error('❌ Erro ao inicializar WebRTC:', error);
      return { success: false, error: error.message };
    }
    } catch (error) {
      console.error('❌ Erro ao inicializar WebRTC Service:', error);
      return { success: false, error: error.message };
    }
  }

  _nextDebugId(prefix) {
    this._debugSeq += 1;
    return `${prefix}-${Date.now()}-${this._debugSeq}-${Math.random().toString(16).slice(2, 8)}`;
  }

  _dbg(session, message, data = null, level = 'log') {
    const sid = session?.__debugId || 'no-session';
    const outboundId = session?.__outboundDebugId || this._lastOutboundCallDebugId || null;
    const prefix = `[webrtc][${sid}${outboundId ? `][out:${outboundId}` : ''}]`;
    try {
      if (data) {
        console[level](prefix, message, data);
      } else {
        console[level](prefix, message);
      }
    } catch (e) {
      console.log(prefix, message);
    }
  }

  _getRemoteIdentity(session) {
    const identity = session?.remote_identity;
    const phone = identity?.uri?.user || '';
    const displayName = identity?.display_name || phone || 'Desconhecido';
    return { phone: phone || displayName, displayName };
  }

  _isSessionAlive(session) {
    if (!session) return false;
    try {
      if (typeof session.isEnded === 'function' && session.isEnded()) {
        return false;
      }
    } catch (e) {}
    // JsSIP: 7 = CANCELED, 8 = TERMINATED (9 = CONFIRMED, still alive)
    if (session.status === 7 || session.status === 8) {
      return false;
    }
    return true;
  }

  _pruneDeadSessions() {
    if (this.currentSession && !this._isSessionAlive(this.currentSession)) {
      this.currentSession = null;
    }
    if (this.incomingSession && !this._isSessionAlive(this.incomingSession)) {
      this.incomingSession = null;
    }
  }

  _notifyOccupancy() {
    this._pruneDeadSessions();
    if (this.onOccupancyChange) {
      try {
        this.onOccupancyChange(this.getStatus());
      } catch (error) {
        console.error('❌ Erro em onOccupancyChange:', error);
      }
    }
  }

  setCampaignActive(active) {
    this._campaignActive = !!active;
    this._notifyOccupancy();
  }

  isCampaignActive() {
    return !!this._campaignActive;
  }

  requestStopCampaign() {
    if (this.onStopCampaign) {
      this.onStopCampaign();
    }
    this.setCampaignActive(false);
  }

  isBusy() {
    this._pruneDeadSessions();
    return !!(this._isSessionAlive(this.currentSession) || this._dialing || this._campaignActive);
  }

  handleIncomingSession(session) {
    if (this.incomingSession && this.incomingSession !== session) {
      this._dbg(session, 'Incoming recusado: já existe outra chamada recebida', null, 'warn');
      try {
        session.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
      } catch (e) {}
      return;
    }

    this.incomingSession = session;
    const remote = this._getRemoteIdentity(session);
    session.__incomingInfo = remote;
    this._dbg(session, '📲 Chamada recebida', remote);

    const clearIncoming = () => {
      if (this.incomingSession === session) {
        this.incomingSession = null;
      }
      this.stopRingtone();
      if (!session.__answered && this.onIncomingCallEnded) {
        this.onIncomingCallEnded();
      }
      this._notifyOccupancy();
    };

    session.on('ended', clearIncoming);
    session.on('failed', clearIncoming);

    if (!this.isBusy()) {
      this.startRingtone();
    }

    if (this.onIncomingCall) {
      this.onIncomingCall({
        ...remote,
        busy: this.isBusy(),
        hasActiveCall: !!this.currentSession,
        campaignActive: this.isCampaignActive()
      });
    }
    this._notifyOccupancy();
  }

  async answerIncomingCall() {
    this._pruneDeadSessions();
    const session = this.incomingSession;
    if (!session || !this._isSessionAlive(session)) {
      this.incomingSession = null;
      return { success: false, error: 'Nenhuma chamada recebida' };
    }
    if (this._isSessionAlive(this.currentSession) || this._dialing) {
      return { success: false, error: 'Já existe uma chamada ativa. Desligue para atender.' };
    }
    if (this._campaignActive) {
      return { success: false, error: 'Há uma campanha em andamento. Encerre a campanha para atender.' };
    }

    this._dialing = true;
    try {
      let mediaStream = null;
      if (!navigator.mediaDevices?.getUserMedia) {
        return { success: false, error: 'Navegador sem suporte a microfone/WebRTC' };
      }
      try {
        mediaStream = await Promise.race([
          navigator.mediaDevices.getUserMedia({ audio: true, video: false }),
          new Promise((_, reject) => {
            setTimeout(() => reject(new Error('timeout')), 25000);
          })
        ]);
        this._outboundStream = mediaStream;
      } catch (mediaError) {
        const message = mediaError?.message === 'timeout'
          ? 'Tempo esgotado ao pedir o microfone. Permita o acesso no navegador e tente de novo.'
          : 'Permita o microfone no navegador para atender';
        return { success: false, error: message };
      }

      if (this.incomingSession !== session) {
        try {
          mediaStream.getTracks().forEach((track) => track.stop());
        } catch (e) {}
        this._outboundStream = null;
        return { success: false, error: 'A chamada recebida já foi encerrada' };
      }

      session.__answered = true;
      this.stopRingtone();
      this.incomingSession = null;
      this.handleSession(session);
      this.callStartTime = new Date();

      const remote = session.__incomingInfo || this._getRemoteIdentity(session);
      if (this.onCallStarted) {
        this.onCallStarted({
          direction: 'inbound',
          phone: remote.phone,
          displayName: remote.displayName
        });
      }

      try {
        session.answer({
          mediaStream,
          mediaConstraints: { audio: true, video: false },
          pcConfig: {
            rtcpMuxPolicy: 'require',
            iceServers: [
              { urls: 'stun:stun.l.google.com:19302' }
            ],
            iceTransportPolicy: 'all'
          }
        });
      } catch (answerError) {
        this.cleanup(session);
        return { success: false, error: answerError.message || 'Falha ao atender a chamada' };
      }

      this._notifyOccupancy();
      return { success: true };
    } catch (error) {
      console.error('❌ Erro ao atender chamada:', error);
      return { success: false, error: error.message };
    } finally {
      this._dialing = false;
    }
  }

  rejectIncomingCall() {
    const session = this.incomingSession;
    if (!session) {
      return { success: false };
    }
    this.stopRingtone();
    try {
      session.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
    } catch (e) {
      try {
        session.terminate();
      } catch (err) {}
    }
    this.incomingSession = null;
    if (this.onIncomingCallEnded) {
      this.onIncomingCallEnded();
    }
    this._notifyOccupancy();
    return { success: true };
  }

  handleSession(session) {
    this.currentSession = session;

    if (!session.__debugId) {
      session.__debugId = this._nextDebugId('rtc');
    }

    session.__watchdogs = {
      createdAt: Date.now(),
      gotPeerConnection: false,
      gotProgress: false,
      gotAccepted: false,
      gotConfirmed: false,
      gotEnded: false,
      gotFailed: false,
      timers: []
    };

    const addWatchdog = (ms, label, fn) => {
      const t = setTimeout(() => {
        try {
          fn();
        } catch (e) {
          this._dbg(session, `watchdog-error:${label}`, { error: e?.message || String(e) }, 'warn');
        }
      }, ms);
      session.__watchdogs.timers.push(t);
    };

    const clearWatchdogs = () => {
      if (session.__watchdogs?.timers?.length) {
        session.__watchdogs.timers.forEach(t => clearTimeout(t));
      }
      if (session.__iceReadyTimer) {
        clearTimeout(session.__iceReadyTimer);
        session.__iceReadyTimer = null;
      }
      if (session.__statsInterval) {
        clearInterval(session.__statsInterval);
        session.__statsInterval = null;
      }
    };

    const forceIceReady = (reason) => {
      if (session.__iceReadyCalled || session.__watchdogs.gotFailed || session.__watchdogs.gotEnded) {
        return;
      }
      const ready = session.__iceReadyFn;
      if (typeof ready !== 'function') {
        this._dbg(session, `ICE ready skipped (${reason}): INVITE SDP not gathering yet`);
        return;
      }
      session.__iceReadyCalled = true;
      if (session.__iceReadyTimer) {
        clearTimeout(session.__iceReadyTimer);
        session.__iceReadyTimer = null;
      }
      this._dbg(session, `🧊 ICE ready (${reason}) — sending SDP/INVITE`);
      try {
        ready();
      } catch (err) {
        this._dbg(session, 'ICE ready() error', { error: err?.message || String(err) }, 'warn');
      }
    };

    // JsSIP waits for ICE gathering to finish before sending INVITE.
    // STUN can hang indefinitely (no INVITE, only REGISTER/OPTIONS). The
    // tryit.jssip.net webphone calls event.ready() after a short idle window.
    session.on('icecandidate', (event) => {
      session.__iceReadyFn = event.ready;
      if (session.__iceReadyTimer) {
        clearTimeout(session.__iceReadyTimer);
      }
      session.__iceReadyTimer = setTimeout(() => {
        forceIceReady('candidates-idle');
      }, 300);
    });

    session.on('connecting', () => {
      this._dbg(session, '🔗 connecting — mídia local anexada, criando offer');
    });

    session.on('sdp', (e) => {
      this._dbg(session, '📄 SDP', {
        originator: e?.originator,
        type: e?.type,
        sdpLength: e?.sdp?.length
      });
    });

    session.on('sending', (e) => {
      this._dbg(session, '📤 SIP sending', { method: e?.request?.method });
    });

    session.on('getusermediafailed', (error) => {
      this._dbg(session, '❌ getUserMedia failed', {
        error: error?.message || String(error)
      }, 'error');
    });

    const snapshot = (reason) => {
      const pc = session.connection || session.sessionDescriptionHandler?.peerConnection;;
      this._dbg(session, `snapshot:${reason}`, {
        direction: session.direction,
        status: session.status,
        isEstablished: typeof session.isEstablished === 'function' ? session.isEstablished() : null,
        isEnded: typeof session.isEnded === 'function' ? session.isEnded() : null,
        signalingState: pc?.signalingState,
        iceGatheringState: pc?.iceGatheringState,
        iceConnectionState: pc?.iceConnectionState,
        connectionState: pc?.connectionState
      });
    };

    addWatchdog(2500, 'force-ice-invite', () => {
      if (!session.__watchdogs.gotProgress && !session.__watchdogs.gotFailed && !session.__watchdogs.gotEnded) {
        forceIceReady('watchdog-2.5s');
      }
    });

    addWatchdog(8000, 'no-peerconnection', () => {
      if (!session.__watchdogs.gotPeerConnection && !session.__watchdogs.gotFailed && !session.__watchdogs.gotEnded) {
        snapshot('stalled:no-peerconnection');
      }
    });

    addWatchdog(15000, 'no-progress', () => {
      if (!session.__watchdogs.gotProgress && !session.__watchdogs.gotConfirmed && !session.__watchdogs.gotFailed && !session.__watchdogs.gotEnded) {
        snapshot('stalled:no-progress');
      }
    });

    addWatchdog(60000, 'no-confirmed', () => {
      if (!session.__watchdogs.gotConfirmed && !session.__watchdogs.gotFailed && !session.__watchdogs.gotEnded) {
        snapshot('stalled:no-confirmed');
        if (session.status === 0) {
          this._dbg(session, 'INVITE ainda não foi enviado (status 0). Não encerrar — aguardando mídia/ICE.', null, 'warn');
          forceIceReady('watchdog-60s-still-null');
          return;
        }
        console.warn("⚠️ Session stuck, forcing termination");
        try {
          session.terminate();
        } catch(e) {}
    
        this.cleanup(session);
      }
    });

    // Debug: Log all events for debugging (simpler approach)
    // We'll add specific listeners for DTMF events

    // Note: SDP manipulation is handled by JsSIP automatically
    // The key fixes for Firefox/Safari are:
    // 1. rtcpMuxPolicy: 'require' (in makeCall options)
    // 2. Proper remote stream handling (in confirmed event)
    // 3. Explicit audio play() call

    session.on('progress', (e) => {
      session.__watchdogs.gotProgress = true;
      this._dbg(session, '📞 Chamada em progresso...', {
        originator: e?.originator,
        responseStatusCode: e?.response?.status_code,
        cause: e?.cause
      });
      if (session.direction === 'outgoing') {
        if (this.onCallStarted) {
          this.callStartTime = new Date();
          this.onCallStarted({ direction: 'outbound' });
        }
        this.startRingback();
      }
    });

    session.on('accepted', (e) => {
      session.__watchdogs.gotAccepted = true;
      this._dbg(session, '✅ Chamada aceita', {
        originator: e?.originator,
        responseStatusCode: e?.response?.status_code,
        cause: e?.cause
      });
    });

    session.on('confirmed', (e) => {
      session.__watchdogs.gotConfirmed = true;
      this._dbg(session, '✅ Chamada confirmada/conectada');
      
      // Stop ringback tone when call is answered
      this.stopRingback();
      this._syncRemoteAudioFromPc(session.connection);

      // Setup DTMF - use first available audio track from connection
      // Modern WebRTC API: use RTCRtpSender.dtmf property instead of createDTMFSender()
      try {
        const senders = session.connection.getSenders();
        console.log('🎤 Total senders:', senders.length);
        const audioSender = senders.find(s => s.track && s.track.kind === 'audio');
        
        if (audioSender) {
          console.log('🎤 Audio sender encontrado:', audioSender);
          console.log('🎤 Audio sender dtmf property:', audioSender.dtmf);
          
          if (audioSender.dtmf) {
            this.dtmfSender = audioSender.dtmf;
            // Check if DTMF insertion is supported
            if (this.dtmfSender.canInsertDTMF !== undefined) {
              console.log('🎤 canInsertDTMF:', this.dtmfSender.canInsertDTMF);
              if (!this.dtmfSender.canInsertDTMF) {
                console.warn('⚠️ DTMF inserção não suportada neste momento (canInsertDTMF = false)');
                this.dtmfSender = null;
              } else {
                console.log('✅ DTMF sender criado com sucesso (canInsertDTMF = true)');
              }
            } else {
              // canInsertDTMF not available, assume it's supported
              console.log('✅ DTMF sender criado com sucesso (canInsertDTMF não disponível, assumindo suporte)');
            }
          } else {
            console.warn('⚠️ Audio sender não tem propriedade dtmf');
            this.dtmfSender = null;
          }
        } else {
          console.warn('⚠️ Audio sender não encontrado');
        }
      } catch (error) {
        console.warn('⚠️ Erro ao criar DTMF sender:', error);
      }

      if (this.onCallConnected) this.onCallConnected();
    });

    session.on('ended', (e) => {
      session.__watchdogs.gotEnded = true;
      this._dbg(session, '📴 Chamada encerrada', { cause: e.cause });
      clearWatchdogs();
      this.stopRingback();
      this.cleanup(session);
      if (this.onCallEnded) {
        const duration = this.callStartTime 
          ? Math.floor((new Date() - this.callStartTime) / 1000) 
          : 0;
        this.onCallEnded({ cause: e.cause, duration, direction: session.direction === 'incoming' ? 'inbound' : 'outbound' });
      }
      this._notifyOccupancy();
    });

    session.on('failed', (e) => {
      session.__watchdogs.gotFailed = true;
      this._dbg(session, '❌ Chamada falhou', { cause: e.cause, originator: e?.originator }, 'error');
      clearWatchdogs();
      this.stopRingback();
      this.cleanup(session);
      if (this.onCallFailed) this.onCallFailed(e.cause);
      this._notifyOccupancy();
    });

    // Listen for DTMF received from remote caller
    // JsSIP uses 'newDTMF' event for received DTMF tones (SIP INFO method)
    
    session.on('newDTMF', (e) => {
		console.log('📟 newDTMF event received');
     // console.log('📟 newDTMF event received - FULL EVENT:', JSON.stringify(e, null, 2));
     // console.log('📟 newDTMF event keys:', Object.keys(e));
      
      // Try multiple ways to extract the DTMF digit
      let dtmfDigit = null;
      
      // Method 1: e.dtmf.tone (most common)
      if (e.dtmf && e.dtmf.tone) {
        dtmfDigit = e.dtmf.tone;
        console.log('📟 Found digit via e.dtmf.tone:', dtmfDigit);
      }
      // Method 2: e.originator.remote.dtmf.tone
      else if (e.originator && e.originator.remote && e.originator.remote.dtmf && e.originator.remote.dtmf.tone) {
        dtmfDigit = e.originator.remote.dtmf.tone;
        console.log('📟 Found digit via e.originator.remote.dtmf.tone:', dtmfDigit);
      }
      // Method 3: e.tone (direct)
      else if (e.tone) {
        dtmfDigit = e.tone;
        console.log('📟 Found digit via e.tone:', dtmfDigit);
      }
      // Method 4: Check all nested properties
      else {
        console.log('📟 Trying to find digit in nested properties...');
        const searchForDigit = (obj, depth = 0) => {
          if (depth > 3) return null;
          if (typeof obj !== 'object' || obj === null) return null;
          
          for (const key in obj) {
            if (key === 'tone' && /^[0-9*#A-D]$/.test(obj[key])) {
              return obj[key];
            }
            const found = searchForDigit(obj[key], depth + 1);
            if (found) return found;
          }
          return null;
        };
        dtmfDigit = searchForDigit(e);
        if (dtmfDigit) {
          console.log('📟 Found digit via deep search:', dtmfDigit);
        }
      }
      
      if (dtmfDigit) {
        console.log('✅ DTMF recebido do cliente via newDTMF:', dtmfDigit);
        console.log('✅ onDTMFReceived callback exists?', !!this.onDTMFReceived);
        if (this.onDTMFReceived) {
          try {
            this.onDTMFReceived(dtmfDigit);
            console.log('✅ onDTMFReceived callback chamado com sucesso');
          } catch (error) {
            console.error('❌ Erro ao chamar onDTMFReceived:', error);
          }
        } else {
          console.error('❌ onDTMFReceived callback não está definido!');
        }
      } else {
        console.warn('⚠️ DTMF event received but could not extract digit. Full event:', e);
      }
    });

    // Also listen for DTMF via RTP (RFC2833) - this happens in the peerconnection event
    // We'll set up RTP DTMF detection when peer connection is established

    const attachPeerConnectionListeners = (pc) => {
      if (!pc || session.__pcListenersAttached) {
        return;
      }
      session.__pcListenersAttached = true;
      session.__watchdogs.gotPeerConnection = true;
      this._dbg(session, '🔗 Peer connection estabelecida');

      if (pc) {
        const stateDump = () => ({
          signalingState: pc.signalingState,
          iceGatheringState: pc.iceGatheringState,
          iceConnectionState: pc.iceConnectionState,
          connectionState: pc.connectionState
        });

        pc.addEventListener('icecandidate', (evt) => {
          if (evt.candidate) {
            session.__iceCandidateCount = (session.__iceCandidateCount || 0) + 1;
            this._dbg(session, '🧊 ICE candidate', {
              count: session.__iceCandidateCount,
              candidateType: evt.candidate.type,
              protocol: evt.candidate.protocol,
              address: evt.candidate.address,
              port: evt.candidate.port
            });
          } else {
            this._dbg(session, '🧊 ICE candidate: end-of-candidates', {
              count: session.__iceCandidateCount || 0,
              ...stateDump()
            });
          }
        });

        pc.addEventListener('icecandidateerror', (evt) => {
          this._dbg(session, '🧊 ICE candidate error', {
            errorCode: evt?.errorCode,
            errorText: evt?.errorText,
            url: evt?.url,
            address: evt?.address,
            port: evt?.port,
            ...stateDump()
          }, 'warn');
        });

        pc.addEventListener('signalingstatechange', () => {
          this._dbg(session, '📡 signaling state', stateDump());
        });

        pc.addEventListener('connectionstatechange', () => {
          this._dbg(session, '🔌 connection state', stateDump());
        });

        pc.addEventListener('track', (evt) => {
          this._dbg(session, '🎧 ontrack', {
            trackKind: evt?.track?.kind,
            trackId: evt?.track?.id,
            trackMuted: evt?.track?.muted,
            trackEnabled: evt?.track?.enabled,
            trackState: evt?.track?.readyState,
            streams: evt?.streams?.map(s => s.id)
          });
          if (evt?.track) {
            this._replacePlaybackAudioTrack(evt.track);
          } else if (evt?.streams?.[0]) {
            evt.streams[0].getAudioTracks().forEach((t) => this._replacePlaybackAudioTrack(t));
          }
        });

        pc.addEventListener('iceconnectionstatechange', () => {
          this._dbg(session, '🧊 ICE connection state', stateDump());
          if (pc.iceConnectionState === 'failed') {
            this._dbg(session, '❌ ICE connection failed - attempting restart', stateDump(), 'error');
          }
        });

        try {
          pc.getTransceivers().forEach(t => {
            if (t?.receiver?.track?.kind === 'audio' && RTCRtpReceiver.getCapabilities) {
              const codecs = RTCRtpReceiver.getCapabilities('audio')?.codecs || [];
              const opus = codecs.filter(c => c.mimeType === 'audio/opus');
              if (opus.length && t.setCodecPreferences) {
                t.setCodecPreferences(opus);
              }
            }
          });
        } catch (codecErr) {
          this._dbg(session, 'codec preference skipped', { error: codecErr?.message || String(codecErr) }, 'warn');
        }

        pc.addEventListener('icegatheringstatechange', () => {
          this._dbg(session, '🧊 ICE gathering state', stateDump());
        });

        session.__statsInterval = setInterval(async () => {
          try {
            const stats = await pc.getStats();
            let selectedPair = null;
            let localCandidate = null;
            let remoteCandidate = null;
            stats.forEach((r) => {
              if (r.type === 'candidate-pair' && (r.selected || r.nominated) && r.state === 'succeeded') {
                selectedPair = r;
              }
              if (r.type === 'local-candidate') {
                if (selectedPair && r.id === selectedPair.localCandidateId) localCandidate = r;
              }
              if (r.type === 'remote-candidate') {
                if (selectedPair && r.id === selectedPair.remoteCandidateId) remoteCandidate = r;
              }
            });
            if (selectedPair) {
              this._dbg(session, '📊 selected candidate-pair', {
                state: selectedPair.state,
                currentRoundTripTime: selectedPair.currentRoundTripTime,
                availableOutgoingBitrate: selectedPair.availableOutgoingBitrate,
                localCandidateType: localCandidate?.candidateType,
                remoteCandidateType: remoteCandidate?.candidateType,
                localProtocol: localCandidate?.protocol,
                remoteProtocol: remoteCandidate?.protocol
              });
            }
          } catch (err) {
          }
        }, 5000);

        // Try to detect DTMF via RTP (RFC2833)
        // WebRTC doesn't natively support receiving DTMF, but we can try to monitor RTP stats
        try {
          const receivers = pc.getReceivers();
          console.log('🎵 Audio receivers encontrados:', receivers.length);
          
          receivers.forEach((receiver, index) => {
            if (receiver.track && receiver.track.kind === 'audio') {
              console.log(`🎵 Audio receiver ${index} encontrado, monitorando para DTMF RTP`);
              
              // Monitor RTP statistics for DTMF payload type 101
              // Note: This is a workaround - proper DTMF detection requires RTP packet parsing
              setInterval(async () => {
                try {
                  const stats = await pc.getStats(receiver);
                  stats.forEach((report) => {
                    // Look for RTP statistics that might indicate DTMF
                    if (report.type === 'inbound-rtp' && report.payloadType === 101) {
                      console.log('📟 Possível DTMF RTP detectado no stats:', report);
                    }
                  });
                } catch (err) {
                  // Ignore stats errors
                }
              }, 1000); // Check every second
            }
          });
        } catch (error) {
          console.warn('⚠️ Erro ao configurar detecção de DTMF RTP:', error);
        }
      }
    };

    session.on('hold', () => {
      this._dbg(session, '⏸️ hold event');
    });

    session.on('unhold', () => {
      this._dbg(session, '▶️ unhold event');
      this._restoreLocalSenders(session.connection);
      this._syncRemoteAudioFromPc(session.connection);
      this._playRemoteAudio();
    });

    session.on('peerconnection', (e) => {
      attachPeerConnectionListeners(e.peerconnection || session.connection);
    });

    // JsSIP emits "peerconnection" before "newRTCSession" on outbound calls,
    // so the listener above often misses it. Bind immediately if PC exists.
    if (session.connection) {
      attachPeerConnectionListeners(session.connection);
    }

    // Also listen for SIP INFO messages which might contain DTMF
    // Some implementations send DTMF via SIP INFO instead of newDTMF event
    session.on('newInfo', (e) => {
      console.log('📨 newInfo event received - FULL EVENT:', JSON.stringify(e, null, 2));
      console.log('📨 newInfo event keys:', Object.keys(e));
      console.log('📨 e.info:', e.info);
      console.log('📨 e.info?.contentType:', e.info?.contentType);
      console.log('📨 e.info?.body:', e.info?.body);
      
      // Check if this is a DTMF INFO message
      const contentType = (e.info?.contentType || '').toLowerCase();
      const body = e.info?.body || '';
      
      console.log('📨 Checking contentType for DTMF:', contentType);
      
      if (contentType.includes('dtmf') || contentType.includes('application/dtmf') || contentType.includes('application/dtmf-relay')) {
        console.log('✅ DTMF via SIP INFO detected!');
        console.log('📨 Body content:', body);
        
        // Try to extract DTMF digit from INFO message body
        // Common formats: Signal=X, Key=X, Digit=X, Code=X, or just the digit
        const patterns = [
          /Signal[=\s:]*([0-9*#A-D])/i,
          /Key[=\s:]*([0-9*#A-D])/i,
          /Digit[=\s:]*([0-9*#A-D])/i,
          /Code[=\s:]*([0-9*#A-D])/i,
          /Tone[=\s:]*([0-9*#A-D])/i,
          /([0-9*#A-D])/  // Just a single digit (last resort)
        ];
        
        for (const pattern of patterns) {
          const match = body.match(pattern);
          if (match && match[1]) {
            const dtmfDigit = match[1];
            console.log('✅ DTMF digit extraído do SIP INFO:', dtmfDigit);
            if (this.onDTMFReceived) {
              this.onDTMFReceived(dtmfDigit);
            } else {
              console.error('❌ onDTMFReceived callback não está definido!');
            }
            return;
          }
        }
        console.warn('⚠️ DTMF INFO recebido mas não foi possível extrair o dígito. Body:', body);
      } else {
        console.log('📨 SIP INFO não é DTMF, contentType:', contentType);
      }
    });
  }

  async makeCall(phoneNumber, options = {}) {
    if (!this.ua || !this.isRegistered) {
      console.error('❌ UA não está registrado');
      return { success: false, error: 'SIP não registrado' };
    }

    this._pruneDeadSessions();

    if (this._dialing || this._isSessionAlive(this.currentSession)) {
      console.error('❌ Já existe uma chamada ativa');
      return { success: false, error: 'Chamada já ativa' };
    }

    if (this._isSessionAlive(this.incomingSession)) {
      console.error('❌ Há uma chamada recebida');
      return { success: false, error: 'Há uma chamada recebida. Atenda ou recuse antes de ligar.' };
    }

    this._dialing = true;
    try {
      let mediaStream = options.mediaStream || null;
      if (!mediaStream) {
        if (!navigator.mediaDevices?.getUserMedia) {
          return { success: false, error: 'Navegador sem suporte a microfone/WebRTC' };
        }
        console.log('🎤 Solicitando acesso ao microfone...');
        try {
          mediaStream = await Promise.race([
            navigator.mediaDevices.getUserMedia({ audio: true, video: false }),
            new Promise((_, reject) => {
              setTimeout(() => reject(new Error('timeout')), 25000);
            })
          ]);
          this._outboundStream = mediaStream;
          const tracks = mediaStream.getAudioTracks();
          console.log('🎤 Microfone concedido', {
            tracks: tracks.length,
            labels: tracks.map((t) => t.label)
          });
        } catch (mediaError) {
          console.error('❌ Microfone recusado ou indisponível:', mediaError);
          const message = mediaError?.message === 'timeout'
            ? 'Tempo esgotado ao pedir o microfone. Permita o acesso no navegador e tente de novo.'
            : 'Permita o microfone no navegador para ligar';
          return { success: false, error: message };
        }
      }

      const callOptions = {
        mediaStream,
        mediaConstraints: { audio: true, video: false },
        eventHandlers: {
          getusermediafailed: (error) => {
            console.error('❌ getUserMedia failed (JsSIP):', error);
          }
        },
        pcConfig: {
          rtcpMuxPolicy: 'require',
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' }
          ],
          iceTransportPolicy: 'all'
        }
      };

      // Formatar número (remover caracteres especiais)
      const cleanNumber = phoneNumber.replace(/\D/g, '');
      const sipDomain = this.sipDomain || this.ua?.configuration?.uri?.host || this.ua?._configuration?.uri?.host;
      const sipUri = `sip:${cleanNumber}@${sipDomain}`;

      const outboundDebugId = this._nextDebugId('out');
      this._lastOutboundCallDebugId = outboundDebugId;
      
      console.log(`[webrtc][out:${outboundDebugId}] 📞 Iniciando chamada para:`, sipUri, 'from', this.sipUser);
      this.ua.call(sipUri, callOptions);
      return { success: true };
    } catch (error) {
      console.error('❌ Erro ao fazer chamada:', error);
      if (this._outboundStream) {
        try {
          this._outboundStream.getTracks().forEach((track) => track.stop());
        } catch (e) {}
        this._outboundStream = null;
      }
      return { success: false, error: error.message };
    } finally {
      this._dialing = false;
    }
  }

  hangup() {
    this._pruneDeadSessions();
    if (this.currentSession && this.currentSession === this.incomingSession) {
      return { success: false, error: 'Não é possível desligar a chamada recebida por aqui' };
    }
    if (this.currentSession) {
      // Stop ringback before terminating
      this.stopRingback();
      // Mute before terminating to ensure clean hangup
      try {
        this.currentSession.mute({ audio: true });
      } catch (e) {}
      try {
        this.currentSession.terminate();
      } catch (e) {}
      this.dtmfSender = null;
      this.currentSession = null;
      console.log('📞 Chamada encerrada e áudio silenciado');
      this._notifyOccupancy();
      return { success: true };
    }
    return { success: false };
  }

  mute() {
    if (this.currentSession && this.currentSession.isMuted().audio === false) {
      this.currentSession.mute({ audio: true });
      console.log('🔇 Áudio silenciado');
      return { success: true };
    }
    return { success: false };
  }

  unmute() {
    if (this.currentSession && this.currentSession.isMuted().audio === true) {
      this.currentSession.unmute({ audio: true });
      console.log('🔊 Áudio ativado');
      return { success: true };
    }
    return { success: false };
  }

  sendDTMF(digit) {
    if (!this.currentSession) {
      return { success: false, error: 'Nenhuma chamada ativa' };
    }

    try {
      if (this.dtmfSender) {
        this.dtmfSender.insertDTMF(digit, 100, 70);
      } else {
        // Fallback para JsSIP DTMF
        this.currentSession.sendDTMF(digit);
      }
      
      // Notify that DTMF was sent
      if (this.onDTMFSent) {
        this.onDTMFSent(digit);
      }
      
      console.log('📟 DTMF enviado:', digit);
      if (this.onDTMFSent) this.onDTMFSent(digit);
      return { success: true };
    } catch (error) {
      console.error('❌ Erro ao enviar DTMF:', error);
      return { success: false, error: error.message };
    }
  }

  _ensureRemoteAudioElement() {
    if (!this.remoteAudio) {
      this.remoteAudio = new Audio();
      this.remoteAudio.autoplay = true;
      this.remoteAudio.playsInline = true;
      this.remoteAudio.setAttribute('playsinline', 'true');
    }
    this.remoteAudio.muted = false;
    this.remoteAudio.volume = 1;
    return this.remoteAudio;
  }

  _playRemoteAudio() {
    const audio = this._ensureRemoteAudioElement();
    const playPromise = audio.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((err) => {
        console.warn('⚠️ Erro ao reproduzir áudio remoto:', err);
      });
    }
  }

  _replacePlaybackAudioTrack(track) {
    if (!track || track.kind !== 'audio' || track.readyState === 'ended') {
      return;
    }

    track.enabled = true;

    if (!this._playbackStream) {
      this._playbackStream = new MediaStream();
    }

    const current = this._playbackStream.getAudioTracks();
    const alreadyAttached = current.some((t) => t.id === track.id);
    current.forEach((t) => {
      if (t.id !== track.id) {
        this._playbackStream.removeTrack(t);
      }
    });
    if (!alreadyAttached) {
      this._playbackStream.addTrack(track);
    }

    const audio = this._ensureRemoteAudioElement();
    if (!alreadyAttached) {
      // Chrome does not always play a newly added track if srcObject is unchanged.
      audio.srcObject = null;
      audio.srcObject = this._playbackStream;
    } else if (audio.srcObject !== this._playbackStream) {
      audio.srcObject = this._playbackStream;
    }

    track.onunmute = () => {
      this._dbg(this.currentSession, '🎧 remote track unmute');
      track.enabled = true;
      this._playRemoteAudio();
    };
    track.onmute = () => {
      this._dbg(this.currentSession, '🎧 remote track mute');
    };
    track.onended = () => {
      this._dbg(this.currentSession, '🎧 remote track ended');
    };

    if (!track.muted) {
      this._playRemoteAudio();
    }
  }

  _syncRemoteAudioFromPc(pc) {
    if (!pc) return;

    const receivers = typeof pc.getReceivers === 'function' ? pc.getReceivers() : [];
    let attached = false;
    receivers.forEach((receiver) => {
      const track = receiver?.track;
      if (track && track.kind === 'audio' && track.readyState !== 'ended') {
        this._replacePlaybackAudioTrack(track);
        attached = true;
      }
    });

    if (!attached && typeof pc.getRemoteStreams === 'function') {
      const streams = pc.getRemoteStreams() || [];
      streams.forEach((stream) => {
        stream.getAudioTracks().forEach((t) => this._replacePlaybackAudioTrack(t));
      });
    }
  }

  _restoreLocalSenders(pc) {
    if (!pc || typeof pc.getSenders !== 'function') return;
    pc.getSenders().forEach((sender) => {
      if (sender.track && sender.track.kind === 'audio') {
        sender.track.enabled = true;
      }
    });
  }

  hold() {
    if (this.currentSession && !this.currentSession.isOnHold().local) {
      this.currentSession.hold();
      console.log('⏸️ Chamada em espera');
      return { success: true };
    }
    return { success: false };
  }

  unhold() {
    if (this.currentSession && this.currentSession.isOnHold().local) {
      const session = this.currentSession;
      this.currentSession.unhold();
      console.log('▶️ Chamada retomada');
      const resync = () => {
        if (this.currentSession !== session) return;
        this._restoreLocalSenders(session.connection);
        this._syncRemoteAudioFromPc(session.connection);
        this._playRemoteAudio();
      };
      setTimeout(resync, 250);
      setTimeout(resync, 900);
      return { success: true };
    }
    return { success: false };
  }

  transfer(target) {
    if (!this.currentSession) {
      console.error('❌ No active call to transfer');
      return { success: false, error: 'No active call' };
    }

    if (!target) {
      console.error('❌ Transfer target is required');
      return { success: false, error: 'Transfer target required' };
    }

    const digits = String(target).replace(/\D/g, '');
    if (digits.length === 5) {
      return { success: false, error: 'Transferência para ramais (5 dígitos) não é permitida' };
    }

    try {
      const sipDomain = this.sipDomain || this.ua?.configuration?.uri?.host || this.ua?._configuration?.uri?.host;
      const sipUri = `sip:${target}@${sipDomain}`;
      console.log(`📞 Transferring call to: ${sipUri}`);
      this.currentSession.refer(sipUri);
      return { success: true };
    } catch (error) {
      console.error('❌ Error initiating transfer:', error);
      return { success: false, error: error.message };
    }
  }

  cleanup(endedSession = null) {
    this.stopRingback();
    if (endedSession && this.currentSession && this.currentSession !== endedSession) {
      return;
    }
    if (this._outboundStream) {
      try {
        this._outboundStream.getTracks().forEach((track) => track.stop());
      } catch (e) {}
      this._outboundStream = null;
    }
    if (this._playbackStream) {
      this._playbackStream.getTracks().forEach((track) => {
        try {
          this._playbackStream.removeTrack(track);
        } catch (e) {}
      });
      this._playbackStream = null;
    }
    if (this.remoteAudio) {
      this.remoteAudio.pause();
      this.remoteAudio.srcObject = null;
    }
    this.currentSession = null;
    this.dtmfSender = null;
    this.callStartTime = null;
  }

  // Start playing ringback tone (looped)
  startRingback() {
    // Stop any existing ringback first
    this.stopRingback();
    
    try {
      this.ringbackAudio = new Audio('/rbtone.wav');
      this.ringbackAudio.loop = true;
      this.ringbackAudio.volume = 0.7; // Set volume to 70% to avoid being too loud
      
      // Play the ringback tone
      this.ringbackAudio.play().then(() => {
        console.log('🔔 Ringback tone iniciado');
      }).catch(error => {
        console.error('❌ Erro ao reproduzir ringback tone:', error);
        this.ringbackAudio = null;
      });
    } catch (error) {
      console.error('❌ Erro ao criar ringback audio:', error);
      this.ringbackAudio = null;
    }
  }

  // Stop playing ringback tone
    stopRingback() {
      if (this.ringbackAudio) {
        try {
          this.ringbackAudio.pause();
          this.ringbackAudio.src = "";
          this.ringbackAudio.load();
          this.ringbackAudio = null;
          console.log('🔇 Ringback tone parado');
        } catch (error) {
          console.error('❌ Erro ao parar ringback tone:', error);
        }
      }
    }

  startRingtone() {
    this.stopRingtone();
    try {
      this.ringtoneAudio = new Audio('/rbtone.wav');
      this.ringtoneAudio.loop = true;
      this.ringtoneAudio.volume = 0.8;
      this.ringtoneAudio.play().then(() => {
        console.log('🔔 Ringtone de chamada recebida iniciado');
      }).catch(error => {
        console.error('❌ Erro ao reproduzir ringtone:', error);
        this.ringtoneAudio = null;
      });
    } catch (error) {
      console.error('❌ Erro ao criar ringtone:', error);
      this.ringtoneAudio = null;
    }
  }

  stopRingtone() {
    if (this.ringtoneAudio) {
      try {
        this.ringtoneAudio.pause();
        this.ringtoneAudio.src = "";
        this.ringtoneAudio.load();
        this.ringtoneAudio = null;
        console.log('🔇 Ringtone parado');
      } catch (error) {
        console.error('❌ Erro ao parar ringtone:', error);
      }
    }
  }

  disconnect() {
    this._initGeneration += 1;
    if (this.incomingSession) {
      try {
        this.incomingSession.terminate();
      } catch (e) {}
      this.incomingSession = null;
    }
    if (this.currentSession) {
      this.currentSession.terminate();
    }
    if (this.ua) {
      this.ua.stop();
      this.ua = null;
    }
    this.stopRingtone();
    this.cleanup();
    this.isConnected = false;
    this.isRegistered = false;
    console.log('🔌 WebRTC desconectado');
  }

  getStatus() {
    this._pruneDeadSessions();
    const session = this.currentSession;
    const remote = session ? this._getRemoteIdentity(session) : this._getRemoteIdentity(this.incomingSession);
    let isEstablished = false;
    try {
      isEstablished = typeof session?.isEstablished === 'function' ? session.isEstablished() : false;
    } catch (e) {
      isEstablished = false;
    }
    let isMuted = false;
    let isOnHold = false;
    try {
      isMuted = session ? session.isMuted().audio : false;
      isOnHold = session ? session.isOnHold().local : false;
    } catch (e) {}
    return {
      isConnected: this.isConnected,
      isRegistered: this.isRegistered,
      hasActiveCall: this._isSessionAlive(session),
      hasIncomingCall: this._isSessionAlive(this.incomingSession),
      campaignActive: this.isCampaignActive(),
      isBusy: this.isBusy(),
      direction: session?.direction || (this.incomingSession ? 'incoming' : null),
      isEstablished,
      remoteNumber: remote.phone || '',
      remoteDisplayName: remote.displayName || '',
      isMuted,
      isOnHold
    };
  }

  // Play an audio file during an active call
  playAudioFile(audioUrl) {
    if (!this.currentSession || !this.currentSession.connection) {
      console.error('❌ Nenhuma chamada ativa');
      return { success: false, error: 'Nenhuma chamada ativa' };
    }

    try {
      // Stop any currently playing audio
      this.stopPlayback();

      // Get the audio context if it doesn't exist
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      }

      // Create a media element source
      this.audioElement = new Audio(audioUrl);
      this.audioSource = this.audioContext.createMediaElementSource(this.audioElement);
      
      // Get the audio track from the current session
      const senders = this.currentSession.connection.getSenders();
      const audioSender = senders.find(s => s.track && s.track.kind === 'audio');
      
      if (!audioSender) {
        console.error('❌ Nenhum transmissor de áudio encontrado');
        return { success: false, error: 'Nenhum transmissor de áudio encontrado' };
      }

      // Create a media stream destination
      this.mediaDestination = this.audioContext.createMediaStreamDestination();
      this.audioSource.connect(this.mediaDestination);
      
      // Replace the audio track with our custom audio
      const audioTrack = this.mediaDestination.stream.getAudioTracks()[0];
      if (audioTrack) {
        this.originalAudioTrack = audioSender.track; // Store the original track to restore later
        audioSender.replaceTrack(audioTrack);
      }

      // Set up event listeners
      this.audioElement.onended = () => {
        console.log('⏹️ Reprodução de áudio concluída');
        this.stopPlayback();
      };

      // Play the audio
      return this.audioElement.play()
        .then(() => {
          console.log('▶️ Reproduzindo áudio:', audioUrl);
          return { success: true };
        })
        .catch(error => {
          console.error('❌ Erro ao reproduzir áudio:', error);
          return { success: false, error: error.message };
        });
    } catch (error) {
      console.error('❌ Erro ao configurar reprodução de áudio:', error);
      return { success: false, error: error.message };
    }
  }

  // Stop any currently playing audio
  stopPlayback() {
    if (this.audioElement) {
      try {
        this.audioElement.pause();
        this.audioElement.currentTime = 0;
        this.audioElement.onended = null; // Remove the event listener
      } catch (error) {
        console.error('❌ Erro ao parar áudio:', error);
      }
    }

    // Restore the original audio track if it exists
    if (this.currentSession && this.currentSession.connection && this.originalAudioTrack) {
      try {
        const senders = this.currentSession.connection.getSenders();
        const audioSender = senders.find(s => s.track && s.track.kind === 'audio');
        if (audioSender) {
          audioSender.replaceTrack(this.originalAudioTrack);
        }
      } catch (error) {
        console.error('❌ Erro ao restaurar faixa de áudio original:', error);
      }
    }

    // Clean up audio context and related objects
    if (this.audioSource) {
      try {
        this.audioSource.disconnect();
      } catch (error) {
        console.error('❌ Erro ao desconectar fonte de áudio:', error);
      }
      this.audioSource = null;
    }

    if (this.mediaDestination) {
      try {
        this.mediaDestination.disconnect();
      } catch (error) {
        console.error('❌ Erro ao desconectar destino de mídia:', error);
      }
      this.mediaDestination = null;
    }

    // Don't close the audio context as it might be needed again
    // Just clean up the reference to the audio element
    this.audioElement = null;
    this.originalAudioTrack = null;

    return { success: true };
  }
}

export default new WebRTCService();
